import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

export default function CurricularActivityCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      title: "",
      icon: "",
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sort_order: Yup.string().required("Sort Order is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        title: values.title,
        icon: values.icon,
        sort_order: values.sort_order,
        status: values.isActive === "Active",
      };

      try {
        let response;
        if (isEdit) {
          response = await request.put("curricular-activities/" + id, payload);
        } else {
          response = await request.post("curricular-activities/create", payload);
        }

        if (response?.data?.status) {
          toast.success(
            isEdit ? "Activity updated successfully!" : "Activity created successfully!",
          );
          navigate("/admin/curricular-activities");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`curricular-activities/${id}`);
          const item = response.data.data;
          formik.setValues({
            title: item.title || "",
            icon: item.icon || "",
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Activity" : "Create New Activity"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/curricular-activities"),
              }}
              primaryButton={{
                label: isEdit ? "Update Activity" : "Create Activity",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="title" label="Title" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="icon" label="Icon" type="text" formik={formik} placeholder="e.g. emoji_events" />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}